/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Panel, PanelBody, PanelHeader } from '../../../util/panel';
import { Button, Table } from 'antd';
import { FaEye, FaEyeSlash, FaPen, FaPlus } from 'react-icons/fa';
import { RiFileListLine } from 'react-icons/ri';
import { MdDelete } from 'react-icons/md';
import usePaginationHook from '../../../../utils/usePagination';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AlertConfirm, AlertError } from '../../../util/Alert';
import { FormProvider, useForm } from 'react-hook-form';
import 'dayjs/locale/th';
import { useHistory } from 'react-router';
import LegacySurveyModal from '../components/modal/LegacySurveyModal';
import {
  legacySurveyFormDefaultValues,
  legacySurveyFormSchema,
} from '../components/dto/legacy-survey-form.dto';
import fitSurvey2Services from '../../../../services/fit-survey2.services';
import LegacySurveyPartnerListModal from '../components/modal/LegacySurveyPartnerListModal';
import LegacySurveyQrCodeModal from '../components/modal/LegacySurveyQrcodeModal';
import { FaQrcode } from 'react-icons/fa';
import { useClipboard } from 'use-clipboard-copy';
import { FaCopy } from 'react-icons/fa';

dayjs.locale('th');

const pageSize = 10;

const LegacySurvey2Page = () => {
  const [legacySurveyFormOpen, setLegacySurveyFormOpen] = useState(false);
  const [legacySurveyPartnerListOpen, setLegacySurveyPartnerListOpen] =
    useState(false);
  const [legacySurveyQrcodeModalOpen, setLegacySurveyQrcodeModalOpen] =
    useState(false);
  const [survey, setSurvey] = useState(undefined);

  const {
    data: legacySurveyLists,
    setData,
    page,
    setPage,
    total,
    setTotal,
  } = usePaginationHook();
  const queryClient = useQueryClient();
  const history = useHistory();
  const clipboard = useClipboard();

  const legacySurveyForm = useForm({
    defaultValues: legacySurveyFormDefaultValues,
    resolver: legacySurveyFormSchema(),
  });

  const _HandleLegacySurveyFormOpen = (formType) => {
    legacySurveyForm.setValue('formType', formType);
    setLegacySurveyFormOpen(true);
  };

  const _HandleLegacySurveyFormClose = () => {
    legacySurveyForm.reset(legacySurveyFormDefaultValues);
    setLegacySurveyFormOpen(false);
  };

  const { isFetching: legacySurveyListsLoading } =
    fitSurvey2Services.useQueryGetLegacySurveyLists(
      { take: pageSize, page },
      (data) => {
        setData(data.data);
        setTotal(data.meta.total);
      },
    );

  const { mutate: deleteLegacySurvey } =
    fitSurvey2Services.useMutationDeleteLegacySurvey(
      () => {
        queryClient.invalidateQueries({
          queryKey: ['get-legacy-survey2'],
          refetchType: 'all',
        });
      },
      (error) => {
        AlertError({
          title: 'ไม่สามารถดำเนินการได้',
          text: error,
        });
      },
    );

  const { mutate: updateLegacySurveyDisplay } =
    fitSurvey2Services.useMutationUpdateLegacySurveyDisplay(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-legacy-survey2'],
        refetchType: 'all',
      });
    });

  const columns = [
    {
      title: 'NO',
      dataIndex: 'index',
      key: 'index',
      render: (_, record, index) => (
        <span>{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: 'ID',
      dataIndex: 'ID',
      key: 'ID',
      render: (_, record, index) => (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {['group', 'Event'].includes(record.memberType) && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  marginBottom: 10,
                }}
                onClick={() => {
                  clipboard.copy(record.id);
                }}
              >
                <FaCopy style={{ fontSize: 18 }} />
                <span className="ml-2">คัดลอก ID {record.id}</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setSurvey(record);
                  setLegacySurveyQrcodeModalOpen(!legacySurveyQrcodeModalOpen);
                }}
              >
                <FaQrcode style={{ fontSize: 18 }} />
                <span className="ml-2">QR Code สำหรับงานอีเวนต์</span>
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'รูปภาพ',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      render: (_, record) => (
        <div
          style={{
            width: 200,
            height: 100,
          }}
        >
          {record.imageUrl && (
            <img
              alt={record.title}
              src={record.imageUrl}
              style={{
                width: 200,
                height: 100,
              }}
            />
          )}
        </div>
      ),
    },
    {
      title: 'แบบ Pop Up',
      dataIndex: 'isPopUp',
      key: 'isPopUp',
      render: (_, record) => <span>{record.isPopUp ? 'ใช่' : 'ไม่ใช่'}</span>,
    },
    {
      title: 'หัวข้อ',
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => <span>{record.title}</span>,
      width: 250,
    },
    {
      title: 'คำอธิบาย',
      dataIndex: 'description',
      key: 'description',
      render: (_, record) => (
        <span>
          {record.description && record.description.length > 200
            ? record.description.slice(0, 200) + '...'
            : record.description}
        </span>
      ),
      width: 250,
    },
    {
      title: 'ประเภทนักธุรกิจ',
      dataIndex: 'memberType',
      key: 'memberType',
      render: (_, record) => (
        <span>
          {record.memberType === 'all' && 'ทั้งหมดที่เป็นสมาชิก'}
          {record.memberType === 'Distributor' && 'เฉพาะนักธุรกิจ'}
          {record.memberType === 'Member' && 'เฉพาะ VIP'}
          {record.memberType === 'group' && 'เฉพาะรายชื่อ'}
          {record.memberType === 'Event' && 'เฉพาะรายชื่ออีเวนต์'}
        </span>
      ),
    },
    {
      title: 'วันที่เริ่ม',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (_, record) => (
        <span>{dayjs(record.startDate).format('DD/MMM/YYYY เวลา HH:mm')}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.startDate).diff(dayjs(b.startDate), 'D') > 0 ? 1 : -1,
    },
    {
      title: 'วันที่สิ้นสุด',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (_, record) => (
        <span>{dayjs(record.endDate).format('DD/MMM/YYYY เวลา HH:mm')}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.endDate).diff(dayjs(b.updatedAt), 'D') > 0 ? 1 : -1,
    },
    {
      title: 'การมองเห็น',
      dataIndex: 'display',
      key: 'display',
      render: (_, record) => <span>{record.display ? 'เปิด' : 'ปิด'}</span>,
    },
    {
      title: 'อัปเดตเมื่อ',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_, record) => (
        <span>{dayjs(record.updatedAt).format('DD/MMM/YYYY')}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.updatedAt).diff(dayjs(b.updatedAt), 'D') > 0 ? 1 : -1,
    },
    {
      title: 'หัวใจที่ได้รับ',
      dataIndex: 'point',
      key: 'point',
      render: (_, record) => <span>{record.point}</span>,
    },
    {
      title: 'ทั้งหมด',
      dataIndex: 'listLength',
      key: 'listLength',
      render: (_, record) => (
        <span>{record.listTotalNumber > 0 ? record.listTotalNumber : ''}</span>
      ),
    },
    {
      title: 'ตอบแล้ว(รหัส)',
      dataIndex: 'userSurveysLength',
      key: 'userSurveysLength',
      render: (_, record) => <span>{record.userSurveyTotalNumber}</span>,
    },
    {
      title: 'ยังไม่ตอบ(รหัส)',
      dataIndex: 'dif',
      key: 'dif',
      render: (_, record) => (
        <span>
          {record.listTotalNumber > 0
            ? record.listTotalNumber - record.userSurveyTotalNumber
            : ''}
        </span>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {['group', 'Event'].includes(record.memberType) && (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={() => {
                setSurvey(record);
                setLegacySurveyPartnerListOpen(!legacySurveyPartnerListOpen);
              }}
            >
              <RiFileListLine />
            </div>
          )}
          {record.display ? (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('ปิดการแสดงหมวดหมู่คำถาม');
                if (confirm)
                  updateLegacySurveyDisplay({
                    id: record.id,
                    display: false,
                  });
              }}
            >
              <FaEye />
            </div>
          ) : (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('เปิดการแสดงหมวดหมู่คำถาม');
                if (confirm)
                  updateLegacySurveyDisplay({
                    id: record.id,
                    display: true,
                  });
              }}
            >
              <FaEyeSlash />
            </div>
          )}
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={() => {
              legacySurveyForm.setValue('id', record.id);
              _HandleLegacySurveyFormOpen('edit');
            }}
          >
            <FaPen />
          </div>
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={async () => {
              const confirm = await AlertConfirm('ลบข้อมูล');
              if (confirm) deleteLegacySurvey({ id: record.id });
            }}
          >
            <MdDelete />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Panel>
      <PanelHeader>แบบสอบถาม</PanelHeader>
      <PanelBody>
        <div style={{ display: 'flex', alignItems: 'center' }} className="mb-2">
          <div style={{ flex: 1 }} />
          <Button
            onClick={() => {
              _HandleLegacySurveyFormOpen('create');
            }}
            type="primary"
          >
            <FaPlus className="mr-2" />
            เพิ่มแบบสอบถาม
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={legacySurveyLists}
          loading={legacySurveyListsLoading}
          style={{ backgroundColor: '#fff' }}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          onChange={({ current }) => {
            setPage(current);
          }}
          pagination={{
            pageSize,
            total,
          }}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                history.push(`/questionaire/survey2/${record.id}`);
              },
            };
          }}
        />
      </PanelBody>
      <FormProvider {...legacySurveyForm}>
        <LegacySurveyModal
          open={legacySurveyFormOpen}
          handleClose={_HandleLegacySurveyFormClose}
        />
      </FormProvider>
      {survey && (
        <>
          <LegacySurveyQrCodeModal
            open={legacySurveyQrcodeModalOpen}
            handleClose={() => {
              setSurvey(undefined);
              setLegacySurveyQrcodeModalOpen(false);
            }}
            survey={survey}
          />
          <LegacySurveyPartnerListModal
            open={legacySurveyPartnerListOpen}
            handleClose={() => {
              setSurvey(undefined);
              setLegacySurveyPartnerListOpen(false);
            }}
            survey={survey}
          />
        </>
      )}
    </Panel>
  );
};

export default LegacySurvey2Page;
